import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

export interface DonutSlice {
  key: string;
  label: string;
  /** Cor da fatia, explícita. Ver a armadilha de dark mode abaixo. */
  color: string;
  value: number;
}

interface DonutProps {
  slices: DonutSlice[];
  /**
   * Base do cálculo das frações. Default: a soma das fatias (donut de composição).
   * Explícito quando o donut é **progresso** — uma fatia de 60 sobre 100 tem de
   * ocupar 60% do anel, não o anel inteiro.
   */
  total?: number;
  /** Lado renderizado em px. O `viewBox` é sempre 100 × 100. */
  size?: number;
  thickness?: number;
  radius?: number;
  trackColor?: string;
  /**
   * `butt` por padrão: com `round`, as pontas de fatias adjacentes vazam uma
   * sobre a outra. `round` só é seguro com **uma** fatia (era o caso do
   * GoLiveReadiness, removido na #1044; hoje nenhum consumidor passa `round`).
   */
  linecap?: "butt" | "round";
  /**
   * Tooltip nativo por fatia (`<title>`). **Opt-in**: a origem do donut não tinha
   * nenhum, e ligá-lo por default acrescentaria hover onde antes não havia.
   */
  sliceTitles?: boolean;
  /**
   * Clique por fatia, **opt-in**: com ele a fatia vira atalho de mouse para o que a
   * legenda faz. A legenda continua sendo o caminho de teclado; a fatia não entra
   * na ordem de foco, para o mesmo destino não aparecer duas vezes.
   */
  onSliceClick?: (key: string) => void;
  /**
   * Espaço entre fatias, em px de tela (a EF-FUNC-009 pede 2). **Opt-in**: zero
   * por padrão, para os cards que já existiam não mudarem. Cada fatia perde o
   * espaço e começa meio espaço depois da fronteira; com uma fatia só, o anel
   * fecha sem espaço, e fatia menor que o espaço some em vez de virar arco negativo.
   */
  gap?: number;
  ariaLabel?: string;
  className?: string;
  /** Centro do donut — o que era `{value}%` / "Ready" hardcoded na origem. */
  children?: ReactNode;
}

/**
 * Extraído do `DonutChart` do `GoLiveReadiness` (removido na #1044), de 1 arco
 * para N fatias.
 *
 * ⚠️ A cor vem **por fatia**, nunca de `--primary`/`--warning`: as duas são gold
 * no tema escuro e fatias vizinhas ficariam indistinguíveis.
 *
 * `radius` tem default 40 no `viewBox` de 100 — a mesma proporção da origem. A
 * circunferência é derivada aqui dentro; o pai não a calcula mais.
 */
export function Donut({
  slices,
  total: totalProp,
  size = 90,
  thickness = 8,
  radius = 40,
  trackColor = "hsl(var(--muted))",
  linecap = "butt",
  sliceTitles = false,
  onSliceClick,
  gap = 0,
  ariaLabel,
  className,
  children,
}: DonutProps) {
  const circumference = 2 * Math.PI * radius;
  const sum = slices.reduce((acc, s) => acc + Math.max(0, s.value ?? 0), 0);
  const total = totalProp != null && totalProp > 0 ? totalProp : sum;
  // O viewBox tem 100 de lado e a tela `size` px: o espaço em px vira unidade de arco.
  const visible = slices.filter((s) => Math.max(0, s.value ?? 0) > 0).length;
  const gapUnits = visible > 1 ? (gap * 100) / size : 0;

  let start = 0;

  return (
    <div className={cn("relative", className)}>
      <svg
        width={size}
        height={size}
        viewBox="0 0 100 100"
        className="-rotate-90"
        role={ariaLabel ? "img" : undefined}
        aria-label={ariaLabel}
      >
        <circle
          data-track="true"
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          stroke={trackColor}
          strokeWidth={thickness}
        />
        {total > 0 &&
          slices.map((slice) => {
            const value = Math.max(0, slice.value ?? 0);
            // Fatia de valor 0 **continua no DOM** com arco de comprimento zero
            // (invisível), para quem anima de 0 até o valor final ter o elemento
            // desde o primeiro frame. Quem animava era o GoLiveReadiness, removido
            // na #1044; hoje nenhum consumidor anima, mas o contrato tem teste.
            const fraction = value / total;
            const arc = Math.max(0, fraction * circumference - gapUnits);
            // `strokeDashoffset` codifica **posição inicial acumulada**, não
            // progresso: cada fatia começa onde a anterior terminou (mais meio
            // espaço, quando há espaço entre fatias).
            const offset = -(start * circumference + (arc > 0 ? gapUnits / 2 : 0));
            start += fraction;
            return (
              <circle
                key={slice.key}
                data-slice={slice.key}
                cx="50"
                cy="50"
                r={radius}
                fill="none"
                stroke={slice.color}
                strokeWidth={thickness}
                strokeLinecap={linecap}
                strokeDasharray={`${arc} ${circumference - arc}`}
                strokeDashoffset={offset}
                onClick={onSliceClick ? () => onSliceClick(slice.key) : undefined}
                className={onSliceClick ? "cursor-pointer" : undefined}
              >
                {sliceTitles && <title>{`${value} ${slice.label}`}</title>}
              </circle>
            );
          })}
      </svg>
      {/* `pointer-events-none`: o centro cobre o SVG inteiro e engolia o mouse, e
          nenhuma fatia recebia hover nem clique. */}
      {children && (
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          {children}
        </div>
      )}
    </div>
  );
}
